"use client";

import { useEffect, useState } from "react";
import { Icon } from "./Icon";
import { cn } from "@/lib/utils";

interface Complaint {
  id: string;
  createdAt: string;
  status: "ouverte" | "a_valider" | "traitee";
  who: string;
  motif: string;
  urgency: "haute" | "moyenne" | "basse";
  transmission: string;
  phone?: string;
  customerEmail?: string;
  draftResponse?: string;
  draftEmail?: string;
  draftPhone?: string;
  draftChannel?: "email" | "sms" | "whatsapp";
  draftedAt?: string;
  resolvedAt?: string;
  // Tableau pour les reclamations traitees depuis le passage au multi-canal ;
  // simple chaine possible pour les reclamations traitees avant (donnees
  // historiques) -- voir resolvedViaList().
  resolvedVia?: ("email" | "sms" | "whatsapp")[] | "email" | "sms" | "whatsapp";
  reopenedAt?: string;
  reopenedCount?: number;
}

const URGENCY_STYLE: Record<Complaint["urgency"], string> = {
  haute: "bg-red-50 text-red-700 border-red-200",
  moyenne: "bg-amber-50 text-amber-700 border-amber-200",
  basse: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export function ComplaintsBoard() {
  const [complaints, setComplaints] = useState<Complaint[] | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<string | null>(null);

  const load = async () => {
    const r = await fetch("/api/complaints");
    const j = await r.json();
    setComplaints(j.complaints ?? []);
  };

  useEffect(() => {
    load();
  }, []);

  const checkReplies = async () => {
    setChecking(true);
    setCheckResult(null);
    try {
      const r = await fetch("/api/complaints/check-replies", { method: "POST" });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Erreur");
      setCheckResult(
        j.reopened > 0
          ? `${j.reopened} réclamation${j.reopened > 1 ? "s" : ""} rouverte${j.reopened > 1 ? "s" : ""} (réponse client détectée).`
          : `Aucune nouvelle réponse client (${j.checked} vérifiée${j.checked > 1 ? "s" : ""}).`
      );
      await load();
    } catch (e) {
      setCheckResult(e instanceof Error ? e.message : "Erreur de vérification");
    } finally {
      setChecking(false);
    }
  };

  if (complaints === null) {
    return <div className="text-sm text-[var(--color-muted)]">Chargement…</div>;
  }

  const toValidate = complaints.filter((c) => c.status === "a_valider");
  const open = complaints.filter((c) => c.status === "ouverte");
  const resolved = complaints.filter((c) => c.status === "traitee");

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-4 text-sm">
          {toValidate.length > 0 && (
            <span className="flex items-center gap-1.5 font-bold text-amber-700">
              <span className="h-2 w-2 rounded-full bg-amber-500" /> {toValidate.length} à valider
            </span>
          )}
          <span className="flex items-center gap-1.5 font-bold text-[#0a1410]">
            <span className="h-2 w-2 rounded-full bg-red-500" /> {open.length} ouverte{open.length !== 1 ? "s" : ""}
          </span>
          <span className="flex items-center gap-1.5 font-bold text-[#0a1410]">
            <span className="h-2 w-2 rounded-full bg-emerald-500" /> {resolved.length} traitée{resolved.length !== 1 ? "s" : ""}
          </span>
        </div>
        <button
          type="button"
          onClick={checkReplies}
          disabled={checking}
          className="flex items-center gap-1.5 rounded-lg border border-[#0a1410]/15 bg-white px-3 py-1.5 text-xs font-bold text-[#0a1410] hover:bg-[var(--color-bg-soft)] disabled:opacity-60"
        >
          <Icon name={checking ? "Loader" : "RefreshCw"} size={12} className={checking ? "animate-spin" : ""} />
          {checking ? "Vérification…" : "Vérifier les réponses mail"}
        </button>
      </div>

      {checkResult && (
        <div className="rounded-lg border border-[#0a1410]/10 bg-[var(--color-bg-soft)] px-3 py-2 text-xs text-[#1e3a2c]">
          {checkResult}
        </div>
      )}

      {complaints.length === 0 && (
        <div className="rounded-2xl border border-[#0a1410]/10 bg-white px-6 py-10 text-center text-sm text-[var(--color-muted)]">
          Aucune réclamation pour le moment. Elles apparaissent ici dès que Jojo route un cas vers Mimo.
        </div>
      )}

      {toValidate.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-widest text-amber-700">À valider avant envoi</h3>
          {toValidate.map((c) => (
            <ValidationCard key={c.id} c={c} onDone={load} />
          ))}
        </div>
      )}

      <div className="space-y-3">
        {[...open, ...resolved].map((c) => (
          <ComplaintCard key={c.id} c={c} onDone={load} />
        ))}
      </div>
    </div>
  );
}

/** Réclamation avec une réponse déjà rédigée par Mimo (pipeline automatique
 *  des appels), en attente de validation humaine avant tout envoi réel — voir
 *  lib/callPipeline/run.ts, qui ne fait jamais d'envoi sans passer par ici. */
const CHANNEL_LABEL: Record<"email" | "sms" | "whatsapp", string> = {
  email: "email",
  sms: "SMS",
  whatsapp: "WhatsApp",
};

/** Normalise resolvedVia en tableau -- des reclamations traitees AVANT le
 *  passage au multi-canal ont cette valeur stockee comme simple chaine
 *  ("sms") dans data/complaints.json, pas comme tableau. */
function resolvedViaList(v: unknown): ("email" | "sms" | "whatsapp")[] {
  if (!v) return [];
  return Array.isArray(v) ? v : [v as "email" | "sms" | "whatsapp"];
}

/** Heuristique locale (miroir de isFrenchNumber cote serveur) pour reagir en
 *  direct aux modifications du numero dans le formulaire, sans dependre d'un
 *  aller-retour API. Un champ vide est considere neutre (pas encore "etranger"). */
function isFrenchPhoneLocal(phone: string): boolean {
  const trimmed = phone.trim().replace(/[\s.-]/g, "");
  if (!trimmed) return true;
  let e164 = trimmed;
  if (e164.startsWith("00")) e164 = `+${e164.slice(2)}`;
  else if (e164.startsWith("0") && e164.length === 10) e164 = `+33${e164.slice(1)}`;
  else if (!e164.startsWith("+")) e164 = `+${e164}`;
  return e164.startsWith("+33");
}

type ChannelKey = "email" | "sms" | "whatsapp";

function ValidationCard({ c, onDone }: { c: Complaint; onDone: () => void }) {
  // Selection MULTIPLE -- on peut envoyer par email ET SMS/WhatsApp en meme
  // temps, ce ne sont pas des canaux exclusifs. Presélection : le canal
  // "préféré" suggéré par le pipeline, plus l'email s'il est connu.
  const [channels, setChannels] = useState<Set<ChannelKey>>(() => {
    const initial = new Set<ChannelKey>();
    if (c.draftChannel) initial.add(c.draftChannel);
    if (c.draftEmail) initial.add("email");
    if (initial.size === 0) initial.add(c.draftPhone ? "sms" : "email");
    return initial;
  });
  const [text, setText] = useState(c.draftResponse ?? "");
  const [emailTo, setEmailTo] = useState(c.draftEmail ?? "");
  const [phoneTo, setPhoneTo] = useState(c.draftPhone ?? "");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleChannel = (ch: ChannelKey) => {
    setChannels((prev) => {
      const next = new Set(prev);
      if (next.has(ch)) next.delete(ch);
      else next.add(ch);
      return next;
    });
  };

  // Numero etranger detecte en direct sur la saisie -- desactive le SMS
  // (souvent peu fiable/couteux a l'international) et bascule automatiquement
  // sur WhatsApp des que le numero change vers un format non francais.
  const phoneIsForeign = phoneTo.trim().length > 0 && !isFrenchPhoneLocal(phoneTo);
  useEffect(() => {
    if (phoneIsForeign && channels.has("sms")) {
      setChannels((prev) => {
        const next = new Set(prev);
        next.delete("sms");
        next.add("whatsapp");
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneIsForeign]);

  const needsEmail = channels.has("email");
  const needsPhone = channels.has("sms") || channels.has("whatsapp");
  const canApprove =
    channels.size > 0 &&
    text.trim().length > 0 &&
    (!needsEmail || emailTo.trim().length > 0) &&
    (!needsPhone || phoneTo.trim().length > 0);

  const approve = async () => {
    setSending(true);
    setError(null);
    try {
      const r = await fetch("/api/complaints/approve", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: c.id,
          channels: Array.from(channels),
          editedText: text,
          editedEmailTo: emailTo,
          editedPhoneTo: phoneTo,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Échec de l'envoi.");
      if (j.failed?.length) {
        setError(
          `Envoyé partiellement — échec sur ${j.failed.map((f: { channel: string }) => CHANNEL_LABEL[f.channel as ChannelKey]).join(", ")}.`
        );
      }
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  const channelSummary = Array.from(channels)
    .map((ch) => CHANNEL_LABEL[ch])
    .join(" + ");

  return (
    <div className="rounded-2xl border border-amber-300 bg-amber-50/40 p-5 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.3)]">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[15px] font-bold text-[#0a1410]">{c.who}</span>
            <span className={cn("rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase", URGENCY_STYLE[c.urgency])}>
              {c.urgency}
            </span>
          </div>
          <p className="mt-1 text-[13px] text-[#1e3a2c]">{c.motif}</p>
        </div>
        <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-[11px] font-bold text-amber-800">
          <Icon name="Clock" size={12} /> À valider
        </span>
      </div>

      <div className="mt-3 space-y-2">
        <label className="text-[10px] font-semibold uppercase tracking-widest text-[var(--color-muted)]">
          Canaux d'envoi (vous pouvez en choisir plusieurs à la fois)
        </label>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => toggleChannel("email")}
            disabled={sending}
            className={cn(
              "flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-bold disabled:opacity-40",
              channels.has("email") ? "border-[#0a1410] bg-[#0a1410] text-white" : "border-[#0a1410]/15 bg-white text-[#0a1410] hover:bg-[var(--color-bg-soft)]"
            )}
          >
            <Icon name={channels.has("email") ? "CheckSquare" : "Square"} size={12} />
            <Icon name="Mail" size={12} /> Email
          </button>
          <button
            type="button"
            onClick={() => toggleChannel("sms")}
            disabled={sending || phoneIsForeign}
            className={cn(
              "flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-bold disabled:opacity-40",
              channels.has("sms") ? "border-[#0a1410] bg-[#0a1410] text-white" : "border-[#0a1410]/15 bg-white text-[#0a1410] hover:bg-[var(--color-bg-soft)]"
            )}
          >
            <Icon name={channels.has("sms") ? "CheckSquare" : "Square"} size={12} />
            <Icon name="MessageSquare" size={12} /> SMS {phoneIsForeign && "(numéro étranger)"}
          </button>
          <button
            type="button"
            onClick={() => toggleChannel("whatsapp")}
            disabled={sending}
            className={cn(
              "flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-bold disabled:opacity-40",
              channels.has("whatsapp") ? "border-[#0a1410] bg-[#0a1410] text-white" : "border-[#0a1410]/15 bg-white text-[#0a1410] hover:bg-[var(--color-bg-soft)]"
            )}
          >
            <Icon name={channels.has("whatsapp") ? "CheckSquare" : "Square"} size={12} />
            <Icon name="MessageCircle" size={12} /> WhatsApp
          </button>
        </div>

        {needsEmail && (
          <>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-[var(--color-muted)]">
              Adresse email
            </label>
            <input
              type="text"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              disabled={sending}
              className="w-full rounded-md border border-[#0a1410]/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0a1410]/40 disabled:opacity-60"
            />
          </>
        )}
        {needsPhone && (
          <>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-[var(--color-muted)]">
              Numéro de téléphone (SMS / WhatsApp)
            </label>
            <input
              type="text"
              value={phoneTo}
              onChange={(e) => setPhoneTo(e.target.value)}
              disabled={sending}
              className="w-full rounded-md border border-[#0a1410]/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0a1410]/40 disabled:opacity-60"
            />
          </>
        )}
        <label className="text-[10px] font-semibold uppercase tracking-widest text-[var(--color-muted)]">
          Réponse rédigée par Mimo (modifiable)
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={sending}
          rows={5}
          className="w-full resize-y rounded-md border border-[#0a1410]/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0a1410]/40 disabled:opacity-60"
        />
      </div>

      {error && <p className="mt-2 text-[11px] text-red-700">{error}</p>}

      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={approve}
          disabled={sending || !canApprove}
          className="flex items-center gap-1.5 rounded-md bg-[#0a1410] px-4 py-2 text-xs font-bold text-white hover:bg-[#0a1410]/90 disabled:opacity-50"
        >
          <Icon name={sending ? "Loader" : "Send"} size={12} className={sending ? "animate-spin" : ""} />
          {sending ? "Envoi…" : `Approuver et envoyer par ${channelSummary || "…"}`}
        </button>
      </div>
    </div>
  );
}

function ComplaintCard({ c, onDone }: { c: Complaint; onDone: () => void }) {
  const isOpen = c.status === "ouverte";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateDraft = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await fetch("/api/complaints/draft", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: c.id }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Échec de la génération.");
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const reopen = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await fetch("/api/complaints/reopen", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: c.id }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Échec de la réouverture.");
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={cn(
        "rounded-2xl border bg-white p-5 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.3)]",
        isOpen ? "border-red-200" : "border-emerald-200"
      )}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[15px] font-bold text-[#0a1410]">{c.who}</span>
            <span className={cn("rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase", URGENCY_STYLE[c.urgency])}>
              {c.urgency}
            </span>
            {c.reopenedCount ? (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-700">
                Rouverte ×{c.reopenedCount}
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-[13px] text-[#1e3a2c]">{c.motif}</p>
        </div>
        <span
          className={cn(
            "flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold",
            isOpen ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"
          )}
        >
          <Icon name={isOpen ? "Clock" : "CheckCircle"} size={12} />
          {isOpen ? "Ouverte" : "Traitée"}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-[var(--color-muted)]">
        <span>Reçue le {new Date(c.createdAt).toLocaleString("fr-FR")}</span>
        {c.phone && <span>Tél. {c.phone}</span>}
        {c.customerEmail && <span>Mail {c.customerEmail}</span>}
        {c.resolvedAt && (
          <span>
            Traitée le {new Date(c.resolvedAt).toLocaleString("fr-FR")}{" "}
            {(() => {
              const via = resolvedViaList(c.resolvedVia);
              return via.length ? `(${via.map((v) => CHANNEL_LABEL[v]).join(" + ")})` : "";
            })()}
          </span>
        )}
      </div>

      {error && <p className="mt-2 text-[11px] text-red-700">{error}</p>}

      <div className="mt-3 flex justify-end">
        {isOpen ? (
          <button
            type="button"
            onClick={generateDraft}
            disabled={busy}
            className="flex items-center gap-1.5 rounded-lg border border-[#0a1410]/15 bg-white px-3 py-1.5 text-xs font-bold text-[#0a1410] hover:bg-[var(--color-bg-soft)] disabled:opacity-60"
          >
            <Icon name={busy ? "Loader" : "Sparkles"} size={12} className={busy ? "animate-spin" : ""} />
            {busy ? "Génération…" : "Générer une réponse (Mimo)"}
          </button>
        ) : (
          <button
            type="button"
            onClick={reopen}
            disabled={busy}
            className="flex items-center gap-1.5 rounded-lg border border-[#0a1410]/15 bg-white px-3 py-1.5 text-xs font-bold text-[#0a1410] hover:bg-[var(--color-bg-soft)] disabled:opacity-60"
          >
            <Icon name={busy ? "Loader" : "RotateCcw"} size={12} className={busy ? "animate-spin" : ""} />
            {busy ? "Réouverture…" : "Rouvrir (nouveau retour client)"}
          </button>
        )}
      </div>
    </div>
  );
}
